import type { Font } from 'opentype.js';
import sourceUrl from '../../styles/fonts/source-sans-pro-v11-latin/source-sans-pro-v11-latin-regular.ttf?url';

// Toutes les .ttf du dossier fonts/ (Google Fonts, licences OFL/Apache — usage commercial OK).
// Chargées à la demande (fetch au moment de la sélection), pas dans le bundle initial.
const FONT_URLS = import.meta.glob('./fonts/*.ttf', { eager: true, query: '?url', import: 'default' }) as Record<
	string,
	string
>;
const u = (file: string) => FONT_URLS[`./fonts/${file}`];

export interface TextStyle {
	value: string;
	fontId: string;
	fill: string;
	/** Couleur de contour, '' = pas de contour */
	stroke: string;
	/** Épaisseur du contour en % de la hauteur du texte (0-20) */
	strokePct: number;
}

export interface TextFontDef {
	id: string;
	label: string;
	group: string;
	url: string;
}

const G_SPORT = 'Sport / Impact';
const G_FUN = 'Fun / Comic';
const G_TECHNO = 'Techno / Déco';
const G_SCRIPT = 'Manuscrit / Script';
const G_GOTH = 'Gothique / Horreur';
const G_CLASSIC = 'Classique';

// ⚠️ Toute nouvelle police doit passer `node scripts/validate-fonts.mjs` (parse opentype.js
// 2.0.0 + accents FR + chiffres + path sans NaN + M==Z). Bangers / Black Ops One / Great Vibes /
// Oswald / Paytone One REJETÉES : table GSUB (lookupType 6 substFormat 2) non supportée.
export const TEXT_FONTS: TextFontDef[] = [
	// Anton + Bebas restent en tête : défaut + E2E cliquent dessus dans le dropdown
	{ id: 'anton', label: 'Anton', group: G_SPORT, url: u('Anton-Regular.ttf') },
	{ id: 'bebas', label: 'Bebas Neue', group: G_SPORT, url: u('BebasNeue-Regular.ttf') },
	{ id: 'archivoblack', label: 'Archivo Black', group: G_SPORT, url: u('ArchivoBlack.ttf') },
	{ id: 'alfaslabone', label: 'Alfa Slab One', group: G_SPORT, url: u('AlfaSlabOne.ttf') },
	{ id: 'russoone', label: 'Russo One', group: G_SPORT, url: u('RussoOne.ttf') },
	{ id: 'fjallaone', label: 'Fjalla One', group: G_SPORT, url: u('FjallaOne.ttf') },
	{ id: 'teko', label: 'Teko', group: G_SPORT, url: u('Teko.ttf') },
	{ id: 'staatliches', label: 'Staatliches', group: G_SPORT, url: u('Staatliches.ttf') },
	{ id: 'squadaone', label: 'Squada One', group: G_SPORT, url: u('SquadaOne.ttf') },
	{ id: 'changaone', label: 'Changa One', group: G_SPORT, url: u('ChangaOne.ttf') },
	{ id: 'graduate', label: 'Graduate (collège US)', group: G_SPORT, url: u('Graduate.ttf') },
	{ id: 'passionone', label: 'Passion One', group: G_SPORT, url: u('PassionOne.ttf') },
	{ id: 'lilitaone', label: 'Lilita One', group: G_SPORT, url: u('LilitaOne.ttf') },
	{ id: 'sigmarone', label: 'Sigmar One', group: G_SPORT, url: u('SigmarOne.ttf') },
	{ id: 'titanone', label: 'Titan One', group: G_SPORT, url: u('TitanOne.ttf') },
	{ id: 'ultra', label: 'Ultra', group: G_SPORT, url: u('Ultra.ttf') },
	{ id: 'bowlbyonesc', label: 'Bowlby One SC', group: G_SPORT, url: u('BowlbyOneSC.ttf') },
	{ id: 'rubikmonoone', label: 'Rubik Mono One', group: G_SPORT, url: u('RubikMonoOne.ttf') },
	{ id: 'bigshotone', label: 'Bigshot One', group: G_SPORT, url: u('BigshotOne.ttf') },
	{ id: 'luckiestguy', label: 'Luckiest Guy', group: G_FUN, url: u('LuckiestGuy.ttf') },
	{ id: 'chewy', label: 'Chewy', group: G_FUN, url: u('Chewy.ttf') },
	{ id: 'boogaloo', label: 'Boogaloo', group: G_FUN, url: u('Boogaloo.ttf') },
	{ id: 'concertone', label: 'Concert One', group: G_FUN, url: u('ConcertOne.ttf') },
	{ id: 'carterone', label: 'Carter One', group: G_FUN, url: u('CarterOne.ttf') },
	{ id: 'fugazone', label: 'Fugaz One', group: G_FUN, url: u('FugazOne.ttf') },
	{ id: 'poetsenone', label: 'Poetsen One', group: G_FUN, url: u('PoetsenOne.ttf') },
	{ id: 'sniglet', label: 'Sniglet', group: G_FUN, url: u('Sniglet.ttf') },
	{ id: 'shrikhand', label: 'Shrikhand', group: G_FUN, url: u('Shrikhand.ttf') },
	{ id: 'righteous', label: 'Righteous', group: G_FUN, url: u('Righteous.ttf') },
	{ id: 'knewave', label: 'Knewave', group: G_FUN, url: u('Knewave.ttf') },
	{ id: 'audiowide', label: 'Audiowide', group: G_TECHNO, url: u('Audiowide.ttf') },
	{ id: 'orbitron', label: 'Orbitron', group: G_TECHNO, url: u('Orbitron.ttf') },
	{ id: 'fasterone', label: 'Faster One (vitesse)', group: G_TECHNO, url: u('FasterOne.ttf') },
	{ id: 'racingsansone', label: 'Racing Sans One', group: G_TECHNO, url: u('RacingSansOne.ttf') },
	{ id: 'monoton', label: 'Monoton (néon)', group: G_TECHNO, url: u('Monoton.ttf') },
	{ id: 'bungee', label: 'Bungee', group: G_TECHNO, url: u('Bungee.ttf') },
	{ id: 'bungeeinline', label: 'Bungee Inline', group: G_TECHNO, url: u('BungeeInline.ttf') },
	{ id: 'bungeeshade', label: 'Bungee Shade (3D)', group: G_TECHNO, url: u('BungeeShade.ttf') },
	{ id: 'pressstart2p', label: 'Press Start 2P (pixel)', group: G_TECHNO, url: u('PressStart2P.ttf') },
	{ id: 'pacifico', label: 'Pacifico', group: G_SCRIPT, url: u('Pacifico.ttf') },
	{ id: 'lobster', label: 'Lobster', group: G_SCRIPT, url: u('Lobster.ttf') },
	{ id: 'dancingscript', label: 'Dancing Script', group: G_SCRIPT, url: u('DancingScript.ttf') },
	{ id: 'caveat', label: 'Caveat', group: G_SCRIPT, url: u('Caveat.ttf') },
	{ id: 'satisfy', label: 'Satisfy', group: G_SCRIPT, url: u('Satisfy.ttf') },
	{ id: 'courgette', label: 'Courgette', group: G_SCRIPT, url: u('Courgette.ttf') },
	{ id: 'kaushanscript', label: 'Kaushan Script', group: G_SCRIPT, url: u('KaushanScript.ttf') },
	{ id: 'yellowtail', label: 'Yellowtail', group: G_SCRIPT, url: u('Yellowtail.ttf') },
	{ id: 'sacramento', label: 'Sacramento', group: G_SCRIPT, url: u('Sacramento.ttf') },
	{ id: 'cookie', label: 'Cookie', group: G_SCRIPT, url: u('Cookie.ttf') },
	{ id: 'permanentmarker', label: 'Permanent Marker', group: G_SCRIPT, url: u('PermanentMarker.ttf') },
	{ id: 'shadowsintolight', label: 'Shadows Into Light', group: G_SCRIPT, url: u('ShadowsIntoLight.ttf') },
	{ id: 'amaticsc', label: 'Amatic SC', group: G_SCRIPT, url: u('AmaticSC.ttf') },
	{ id: 'unifraktur', label: 'UnifrakturMaguntia (gothique)', group: G_GOTH, url: u('UnifrakturMaguntia.ttf') },
	{ id: 'pirataone', label: 'Pirata One', group: G_GOTH, url: u('PirataOne.ttf') },
	{ id: 'medievalsharp', label: 'MedievalSharp', group: G_GOTH, url: u('MedievalSharp.ttf') },
	{ id: 'newrocker', label: 'New Rocker', group: G_GOTH, url: u('NewRocker.ttf') },
	{ id: 'metalmania', label: 'Metal Mania', group: G_GOTH, url: u('MetalMania.ttf') },
	{ id: 'creepster', label: 'Creepster', group: G_GOTH, url: u('Creepster.ttf') },
	{ id: 'nosifer', label: 'Nosifer', group: G_GOTH, url: u('Nosifer.ttf') },
	{ id: 'eater', label: 'Eater', group: G_GOTH, url: u('Eater.ttf') },
	{ id: 'source', label: 'Source Sans', group: G_CLASSIC, url: sourceUrl },
	{ id: 'playfair', label: 'Playfair Display', group: G_CLASSIC, url: u('PlayfairDisplay.ttf') },
	{ id: 'abrilfatface', label: 'Abril Fatface', group: G_CLASSIC, url: u('AbrilFatface.ttf') },
	{ id: 'cinzel', label: 'Cinzel', group: G_CLASSIC, url: u('Cinzel.ttf') },
	{ id: 'specialelite', label: 'Special Elite (machine à écrire)', group: G_CLASSIC, url: u('SpecialElite.ttf') },
];

export const TEXT_COLORS = [
	'#111111',
	'#FFFFFF',
	'#D72819',
	'#1D4ED8',
	'#0F766E',
	'#15803D',
	'#F59E0B',
	'#EC4899',
	'#7C3AED',
	'#9CA3AF',
];

const FONT_SIZE = 100;
const fontCache = new Map<string, Promise<Font>>();

// ── Polices importées par le client (Daniel 21.10.2026 : « les clients doivent pouvoir importer leur propre police »)
// Fichier TTF/OTF lu par opentype.js comme les polices du catalogue → texte 100 % vectoriel, identique à l'impression.
// Gardées sur l'appareil (IndexedDB) pour retrouver le texte après un rechargement. Id = 'custom:' + empreinte du fichier.
export const G_CUSTOM = 'Mes polices';
export interface CustomFontDef extends TextFontDef {
	data: ArrayBuffer;
}
const customFonts: CustomFontDef[] = [];
export const listCustomFonts = (): TextFontDef[] => customFonts.map(({ id, label, group, url }) => ({ id, label, group, url }));
export const isCustomFontId = (id: string) => id.startsWith('custom:');

/** Empreinte SHA-256 complète (hex) — la même que celle calculée par le serveur pour les polices du compte. */
export async function fontSha256(buf: ArrayBuffer): Promise<string> {
	try {
		const h = await crypto.subtle.digest('SHA-256', buf);
		return Array.from(new Uint8Array(h)).map(b => b.toString(16).padStart(2, '0')).join('');
	} catch {
		return String(Date.now()).padEnd(64, '0');
	}
}
export const customIdOfSha = (sha256: string) => 'custom:' + sha256.slice(0, 16);

const CUSTOM_DB = 'dtf_v2_fonts';
const CUSTOM_STORE = 'fonts';
const openFontDb = (): Promise<IDBDatabase> =>
	new Promise((resolve, reject) => {
		const req = indexedDB.open(CUSTOM_DB, 1);
		req.onupgradeneeded = () => req.result.createObjectStore(CUSTOM_STORE);
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error);
	});
async function persistCustomFont(f: CustomFontDef): Promise<void> {
	try {
		const db = await openFontDb();
		await new Promise<void>((resolve, reject) => {
			const tx = db.transaction(CUSTOM_STORE, 'readwrite');
			tx.objectStore(CUSTOM_STORE).put({ id: f.id, label: f.label, data: f.data }, f.id);
			tx.oncomplete = () => resolve();
			tx.onerror = () => reject(tx.error);
		});
		db.close();
	} catch {
		/* navigation privée : la police vit le temps de la session */
	}
}
let customLoaded: Promise<void> | null = null;
/** Recharge les polices importées lors d'une visite précédente (appelé à l'ouverture de l'outil texte). */
export function loadCustomFonts(): Promise<void> {
	if (!customLoaded) {
		customLoaded = (async () => {
			try {
				const db = await openFontDb();
				const rows = await new Promise<Array<{ id: string; label: string; data: ArrayBuffer }>>((resolve, reject) => {
					const tx = db.transaction(CUSTOM_STORE, 'readonly');
					const req = tx.objectStore(CUSTOM_STORE).getAll();
					req.onsuccess = () => resolve((req.result || []) as Array<{ id: string; label: string; data: ArrayBuffer }>);
					req.onerror = () => reject(req.error);
				});
				db.close();
				for (const r of rows) if (r && r.id && r.data && !customFonts.some(f => f.id === r.id)) customFonts.push({ id: r.id, label: r.label || r.id, group: G_CUSTOM, url: '', data: r.data });
			} catch {
				/* rien à recharger */
			}
		})();
	}
	return customLoaded;
}
/**
 * Importe un fichier de police (TTF / OTF ; WOFF accepté si opentype.js le lit). Vérifie que la police se lit,
 * dessine lettres + chiffres + accents sans « NaN » (pièges opentype.js 2.0.0). Erreurs : 'illisible' | 'incomplete' | 'trop-gros'.
 */
export async function registerCustomFont(file: File): Promise<TextFontDef> {
	if (file.size > 8 * 1024 * 1024) throw new Error('trop-gros');
	return registerCustomFontBytes(await file.arrayBuffer(), file.name.replace(/\.[a-z0-9]+$/i, ''));
}
/** Les octets d'une police importée sont relisibles (pour la garder avec le compte). */
export const customFontBytes = (id: string): ArrayBuffer | null => (customFonts.find(f => f.id === id) || { data: null }).data;
/** Même vérification qu'un fichier choisi, à partir d'octets (police rapatriée depuis le compte). */
export async function registerCustomFontBytes(data: ArrayBuffer, fallbackName: string): Promise<TextFontDef> {
	const ot = await import('opentype.js');
	let font: Font;
	try {
		font = ot.parse(data);
	} catch {
		throw new Error('illisible');
	}
	let d = '';
	try {
		d = pathToD(font.getPath('ABCxyz 0123456789 éèàçÉ', 0, 0, FONT_SIZE).commands);
	} catch {
		throw new Error('illisible');
	}
	if (!d || /NaN/.test(d)) throw new Error('illisible');
	for (const c of 'ABCabc0123456789') {
		const g = font.charToGlyph(c);
		if (!g || !g.index) throw new Error('incomplete');
	}
	const id = customIdOfSha(await fontSha256(data));
	// opentype.js 2.0 range les noms sous names.windows / names.macintosh (1.x : à plat) → on regarde les trois
	const namesAny = (font.names || {}) as Record<string, unknown>;
	const tables = [namesAny, namesAny.windows, namesAny.macintosh].filter(Boolean) as Array<Record<string, Record<string, string> | undefined>>;
	const pick = (k: string) => {
		for (const tb of tables) {
			const v = tb[k];
			if (v && typeof v === 'object') { const s = v.fr || v.en || Object.values(v)[0]; if (s) return String(s); }
		}
		return '';
	};
	const label = (pick('fullName') || pick('fontFamily') || fallbackName).trim() || fallbackName || 'Ma police';
	const existing = customFonts.find(f => f.id === id);
	if (existing) return { id: existing.id, label: existing.label, group: existing.group, url: '' };
	const def: CustomFontDef = { id, label, group: G_CUSTOM, url: '', data };
	customFonts.push(def);
	fontCache.set(id, Promise.resolve(font));
	await persistCustomFont(def);
	return { id, label, group: G_CUSTOM, url: '' };
}

async function loadFont(fontId: string): Promise<Font> {
	if (isCustomFontId(fontId)) {
		await loadCustomFonts();
		const cf = customFonts.find(f => f.id === fontId);
		if (cf) {
			let pc = fontCache.get(fontId);
			if (!pc) {
				pc = import('opentype.js').then(ot => ot.parse(cf.data));
				fontCache.set(fontId, pc);
			}
			return pc;
		}
	}
	const def = TEXT_FONTS.find(f => f.id === fontId) || TEXT_FONTS[0];
	let p = fontCache.get(def.id);
	if (!p) {
		p = Promise.all([import('opentype.js'), fetch(def.url).then(r => r.arrayBuffer())]).then(([ot, buf]) =>
			ot.parse(buf),
		);
		fontCache.set(def.id, p);
	}
	return p;
}

export interface BuiltText {
	svgText: string;
	src: string;
	aspect: number;
}


// ⚠️ Ne PAS utiliser path.toPathData() : opentype.js 2.0.0 produit « NaN » dès qu'une
// coordonnée a une partie décimale en notation scientifique (ex. 55.00000000000001 avec
// Bebas/Source Sans) → le path SVG s'arrête au 1er glyphe (« Marco » rendu comme « M »).
function pathToD(commands: Array<{ type: string; x?: number; y?: number; x1?: number; y1?: number; x2?: number; y2?: number }>): string {
	const f = (n?: number) => {
		const s = (n || 0).toFixed(2);
		return s === '-0.00' ? '0.00' : s;
	};
	// ⚠️ opentype.js 2.0.0 n'émet JAMAIS de commande Z : chaque contour doit être refermé
	// ici, sinon le stroke pose des butt caps au point de départ (encoche carrée dans le
	// contour, ex. coin bas-droit du « l ») alors que le fill, lui, auto-ferme.
	let d = '';
	let open = false;
	for (const c of commands) {
		if (c.type === 'M') {
			if (open) d += 'Z';
			d += `M${f(c.x)} ${f(c.y)}`;
			open = true;
		} else if (c.type === 'L') d += `L${f(c.x)} ${f(c.y)}`;
		else if (c.type === 'C') d += `C${f(c.x1)} ${f(c.y1)} ${f(c.x2)} ${f(c.y2)} ${f(c.x)} ${f(c.y)}`;
		else if (c.type === 'Q') d += `Q${f(c.x1)} ${f(c.y1)} ${f(c.x)} ${f(c.y)}`;
		else if (c.type === 'Z') {
			d += 'Z';
			open = false;
		}
	}
	if (open) d += 'Z';
	return d;
}

/**
 * Texte → SVG 100 % vectoriel (glyphes convertis en courbes via opentype.js).
 * Le contour est peint DERRIÈRE le remplissage (paint-order) comme sur un maillot.
 */
export async function buildTextSvg(style: TextStyle): Promise<BuiltText> {
	const font = await loadFont(style.fontId);
	const path = font.getPath(style.value, 0, 0, FONT_SIZE);
	const d = pathToD(path.commands);
	if (!d) throw new Error('empty');
	const bb = path.getBoundingBox();
	// stroke-width = 2× l'épaisseur demandée : la moitié intérieure est recouverte par le
	// remplissage → contour VISIBLE = strokePct % de la hauteur, et le halo ponte les
	// interstices entre lettres serrées (sinon échancrures blanches « contour manquant »)
	const strokeW = style.stroke ? 2 * (Math.max(0, Math.min(20, style.strokePct)) / 100) * FONT_SIZE : 0;
	const pad = strokeW / 2 + FONT_SIZE * 0.02;
	const x = bb.x1 - pad;
	const y = bb.y1 - pad;
	const w = bb.x2 - bb.x1 + 2 * pad;
	const h = bb.y2 - bb.y1 + 2 * pad;
	// Contour = tracé séparé SOUS le remplissage (paint-order n'est pas supporté par svg2pdf)
	const strokePath = strokeW
		? `<path d="${d}" fill="${style.stroke}" stroke="${style.stroke}" stroke-width="${strokeW.toFixed(2)}" stroke-linejoin="round"/>`
		: '';
	const svgText =
		// preserveAspectRatio="none" : permet l'étirement volontaire (hauteur sans changer la largeur)
		// width/height explicites : taille intrinsèque → les <img> non contraints gardent le bon ratio
		`<svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" width="${w.toFixed(2)}" height="${h.toFixed(2)}" viewBox="${x.toFixed(2)} ${y.toFixed(2)} ${w.toFixed(2)} ${h.toFixed(2)}">` +
		`${strokePath}<path d="${d}" fill="${style.fill}"/></svg>`;
	const src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgText)))}`;
	return { svgText, src, aspect: w / h };
}

/**
 * Caractères que la police ne sait pas dessiner (glyphe .notdef = tofu) : émojis,
 * alphabets exotiques… Retourne les caractères fautifs (dédupliqués), '' si tout va bien.
 */
export async function findUnsupportedChars(fontId: string, text: string): Promise<string> {
	const font = await loadFont(fontId);
	const bad = new Set<string>();
	for (const c of Array.from(text)) {
		if (/\s/.test(c)) continue;
		try {
			const g = font.charToGlyph(c);
			if (!g || !g.index) bad.add(c);
		} catch {
			bad.add(c);
		}
	}
	return Array.from(bad).join(' ');
}

export const SERIES_MAX = 100;

/** Plage 1-20 → ['1', …, '20'] (bornes dans les deux sens, cap SERIES_MAX). */
export function rangeValues(from: number, to: number): string[] {
	const a = Math.round(from);
	const b = Math.round(to);
	const step = a <= b ? 1 : -1;
	const out: string[] = [];
	for (let v = a; step > 0 ? v <= b : v >= b; v += step) {
		out.push(String(v));
		if (out.length >= SERIES_MAX) break;
	}
	return out;
}

/** Liste collée (Excel / une valeur par ligne, séparateurs ; , tab). */
export function listValues(raw: string): string[] {
	return raw
		.split(/[\n;,\t]+/)
		.map(s => s.trim())
		.filter(Boolean)
		.slice(0, SERIES_MAX);
}
